import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Copy, Printer, Calculator, Info } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Helmet } from "react-helmet-async";

interface CalculationResult {
  shellVolume: number;
  headVolume: number;
  totalVolume: number;
  totalVolumeCubicMeters: number;
}

const TankVolumeCalculator = () => {
  // Tank configuration
  const [tankType, setTankType] = useState<string>("cylindrical");
  const [tankOrientation, setTankOrientation] = useState<string>("vertical");
  const [headType, setHeadType] = useState<string>("");
  
  // Cylindrical tank inputs
  const [diameter, setDiameter] = useState<string>("");
  const [shellHeight, setShellHeight] = useState<string>("");
  
  // Rectangular tank inputs
  const [length, setLength] = useState<string>("");
  const [width, setWidth] = useState<string>("");
  const [height, setHeight] = useState<string>("");
  
  const [results, setResults] = useState<CalculationResult>({ 
    shellVolume: 0, 
    headVolume: 0, 
    totalVolume: 0,
    totalVolumeCubicMeters: 0
  });
  const { toast } = useToast();

  const headTypes = [
    { value: "asme-flanged-dished", label: "ASME Flanged & Dished", description: "Dish Radius = D, Knuckle Radius = 0.06D (Standard for pressure vessels)" },
    { value: "standard-flanged-dished", label: "Standard Flanged & Dished", description: "Dish Radius = D, Knuckle Radius = 0.10D (Common in storage tanks)" },
    { value: "80-10-flanged-dished", label: "80:10 Flanged & Dished", description: "Dish Radius = 0.8D, Knuckle Radius = 0.10D (Shallower profile)" },
    { value: "hemispherical", label: "Hemispherical", description: "Dish Radius = 1/2 Head Inside Diameter (Perfect hemisphere)" },
    { value: "elliptical", label: "Elliptical (2:1)", description: "Semi-ellipsoidal head with 2:1 ratio (Height = D/4)" },
    { value: "conical-10", label: "10° Conical Head", description: "Shallow cone, 10° slope from horizontal (typical for silo/hopper bottoms)" },
    { value: "conical-15", label: "15° Conical Head", description: "Shallow cone, 15° slope from horizontal (typical for silo/hopper bottoms)" },
    { value: "flat", label: "Flat Head", description: "Flat circular plate - no additional volume" }
  ];

  const calculateVolume = () => {
    let shellVolume = 0;
    let headVolume = 0;

    if (tankType === "cylindrical") {
      const dia = parseFloat(diameter);
      const height = parseFloat(shellHeight);
      
      if (!dia || !height) {
        setResults({ shellVolume: 0, headVolume: 0, totalVolume: 0, totalVolumeCubicMeters: 0 });
        return;
      }

      const radius = dia / 2;
      
      // Calculate shell volume (cylinder)
      shellVolume = Math.PI * radius * radius * height;

      // Debug output - Shell calculation
      console.log('=== Tank Volume Calculation Debug ===');
      console.log('Input Diameter (mm):', dia);
      console.log('Input Shell Height (mm):', height);
      console.log('Shell Volume (mm³):', shellVolume);
      console.log('Shell Volume (L):', shellVolume / 1000000);

      // Calculate head volume based on type
      // Test Case Reference:
      // D=1000mm, H=2000mm, ASME F&D → Shell ≈ 1571L, Head ≈ 49L, Total ≈ 1620L
      // D=1000mm, Hemispherical → Head = (2/3) * π * 500³ / 1000000 ≈ 261.8L
      if (headType && headType !== "none") {
        switch (headType) {
          case "asme-flanged-dished":
            // ASME F&D: Ra = D, r = 0.06D
            // Using accurate torispherical formula
            const asmeR = 0.06 * dia; // Knuckle radius
            const asmeRa = dia; // Dish radius
            // Calculate head height using geometry
            const asmeH = asmeRa - Math.sqrt(asmeRa * asmeRa - Math.pow(radius - asmeR, 2)) + asmeR;
            // Volume using ellipsoid segment formula: V = (π/6) * h * (3a² + h²)
            headVolume = (Math.PI / 6) * asmeH * (3 * radius * radius + asmeH * asmeH);
            break;

          case "standard-flanged-dished":
            // Standard F&D: Ra = D, r = 0.10D
            const stdR = 0.10 * dia; // Knuckle radius
            const stdRa = dia; // Dish radius
            const stdH = stdRa - Math.sqrt(stdRa * stdRa - Math.pow(radius - stdR, 2)) + stdR;
            headVolume = (Math.PI / 6) * stdH * (3 * radius * radius + stdH * stdH);
            break;

          case "80-10-flanged-dished":
            // 80:10 F&D: Ra = 0.8D, r = 0.10D
            const fd8010R = 0.10 * dia; // Knuckle radius
            const fd8010Ra = 0.8 * dia; // Dish radius
            const fd8010H = fd8010Ra - Math.sqrt(fd8010Ra * fd8010Ra - Math.pow(radius - fd8010R, 2)) + fd8010R;
            headVolume = (Math.PI / 6) * fd8010H * (3 * radius * radius + fd8010H * fd8010H);
            break;

          case "hemispherical":
            // Hemisphere: V = (2/3) * π * r³
            headVolume = (2 / 3) * Math.PI * Math.pow(radius, 3);
            break;

          case "elliptical":
            // Elliptical head (2:1 ratio): V = (π/6) * h * (3r² + h²), where h = D/4
            const ellipticalHeight = dia / 4;
            headVolume = (Math.PI / 6) * ellipticalHeight * (3 * radius * radius + ellipticalHeight * ellipticalHeight);
            break;

          case "conical-10": {
            // 10° conical head: slope angle from horizontal
            const beta10 = (10 * Math.PI) / 180;
            headVolume = (1 / 3) * Math.PI * Math.pow(radius, 3) * Math.tan(beta10);
            break;
          }

          case "conical-15": {
            // 15° conical head: slope angle from horizontal
            const beta15 = (15 * Math.PI) / 180;
            headVolume = (1 / 3) * Math.PI * Math.pow(radius, 3) * Math.tan(beta15);
            break;
          }

          case "flat":
            // Flat head adds no volume
            headVolume = 0;
            break;

          default:
            headVolume = 0;
        }
        
        // Debug output - Head calculation
        console.log('Head Type:', headType);
        console.log('Head Volume (mm³):', headVolume);
        console.log('Head Volume (L):', headVolume / 1000000);
        console.log('Total Volume (L):', (shellVolume + headVolume) / 1000000);
        console.log('====================================');
      }
    } else if (tankType === "rectangular") {
      const l = parseFloat(length);
      const w = parseFloat(width);
      const h = parseFloat(height);
      
      if (!l || !w || !h) {
        setResults({ shellVolume: 0, headVolume: 0, totalVolume: 0, totalVolumeCubicMeters: 0 });
        return;
      }

      // Rectangular tank volume
      shellVolume = l * w * h;
      headVolume = 0; // Rectangular tanks typically don't have dished heads
    }

    // Convert mm³ to liters (1 liter = 1,000,000 mm³)
    const shellVolumeLiters = shellVolume / 1000000;
    const headVolumeLiters = headVolume / 1000000;
    const totalVolumeLiters = shellVolumeLiters + headVolumeLiters;
    const totalVolumeCubicMeters = totalVolumeLiters / 1000;

    setResults({
      shellVolume: Math.round(shellVolumeLiters * 100) / 100,
      headVolume: Math.round(headVolumeLiters * 100) / 100,
      totalVolume: Math.round(totalVolumeLiters * 100) / 100,
      totalVolumeCubicMeters: Math.round(totalVolumeCubicMeters * 1000) / 1000
    });
  };

  useEffect(() => {
    calculateVolume();
  }, [tankType, diameter, shellHeight, length, width, height, headType]);

  const copyResults = () => {
    const resultText = `Tank Volume Calculator Results:
Tank Type: ${tankType === "cylindrical" ? "Cylindrical" : "Rectangular"}
Tank Orientation: ${tankOrientation === "vertical" ? "Vertical" : "Horizontal"}
${tankType === "cylindrical" ? `Diameter: ${diameter} mm\nShell Height: ${shellHeight} mm` : `Length: ${length} mm\nWidth: ${width} mm\nHeight: ${height} mm`}
${headType && headType !== "none" ? `Head Type: ${headTypes.find(t => t.value === headType)?.label || ""}` : ""}

Shell Volume: ${results.shellVolume} L
Head Volume: ${results.headVolume} L
Total Volume: ${results.totalVolume} L (${results.totalVolumeCubicMeters} m³)`;

    navigator.clipboard.writeText(resultText);
    toast({
      title: "Results copied to clipboard",
      description: "You can now paste the results anywhere.",
    });
  };

  const printResults = () => {
    window.print();
  };

  const getHeadTypeDiagram = (type: string) => {
    // Accurate SVG diagrams for each head type based on industry standards
    const diagrams = {
      "asme-flanged-dished": (
        <svg viewBox="0 0 100 60" className="w-full h-12">
          <defs>
            <mask id="asmeMask">
              <rect x="0" y="0" width="100" height="60" fill="white"/>
              <rect x="0" y="30" width="100" height="30" fill="black"/>
            </mask>
          </defs>
          {/* Straight flange */}
          <line x1="15" y1="50" x2="15" y2="35" stroke="currentColor" strokeWidth="2"/>
          <line x1="85" y1="50" x2="85" y2="35" stroke="currentColor" strokeWidth="2"/>
          {/* ASME F&D profile with proper knuckle and dish radius */}
          <path d="M15 35 Q20 30 25 28 Q50 22 75 28 Q80 30 85 35" fill="none" stroke="currentColor" strokeWidth="2"/>
          {/* Base line */}
          <line x1="15" y1="50" x2="85" y2="50" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      "standard-flanged-dished": (
        <svg viewBox="0 0 100 60" className="w-full h-12">
          {/* Straight flange */}
          <line x1="15" y1="50" x2="15" y2="37" stroke="currentColor" strokeWidth="2"/>
          <line x1="85" y1="50" x2="85" y2="37" stroke="currentColor" strokeWidth="2"/>
          {/* Standard F&D profile */}
          <path d="M15 37 Q22 32 28 30 Q50 25 72 30 Q78 32 85 37" fill="none" stroke="currentColor" strokeWidth="2"/>
          {/* Base line */}
          <line x1="15" y1="50" x2="85" y2="50" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      "80-10-flanged-dished": (
        <svg viewBox="0 0 100 60" className="w-full h-12">
          {/* Straight flange */}
          <line x1="15" y1="50" x2="15" y2="38" stroke="currentColor" strokeWidth="2"/>
          <line x1="85" y1="50" x2="85" y2="38" stroke="currentColor" strokeWidth="2"/>
          {/* 80:10 F&D profile with 80% dish radius and 10% knuckle */}
          <path d="M15 38 Q25 33 30 31 Q50 26 70 31 Q75 33 85 38" fill="none" stroke="currentColor" strokeWidth="2"/>
          {/* Base line */}
          <line x1="15" y1="50" x2="85" y2="50" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      "hemispherical": (
        <svg viewBox="0 0 100 60" className="w-full h-12">
          {/* Perfect hemisphere */}
          <path d="M15 50 A 35 35 0 0 1 85 50" fill="none" stroke="currentColor" strokeWidth="2"/>
          {/* Base line */}
          <line x1="15" y1="50" x2="85" y2="50" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      "elliptical": (
        <svg viewBox="0 0 100 60" className="w-full h-12">
          {/* 2:1 elliptical ratio */}
          <ellipse cx="50" cy="50" rx="35" ry="17.5" fill="none" stroke="currentColor" strokeWidth="2" clipPath="polygon(0 50%, 100% 50%, 100% 0%, 0% 0%)"/>
          {/* Base line */}
          <line x1="15" y1="50" x2="85" y2="50" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      "conical-10": (
        <svg viewBox="0 0 100 60" className="w-full h-12">
          {/* 10° conical head - very shallow triangle (h ≈ 6 at width 70) */}
          <line x1="15" y1="50" x2="15" y2="44" stroke="currentColor" strokeWidth="2"/>
          <line x1="85" y1="50" x2="85" y2="44" stroke="currentColor" strokeWidth="2"/>
          <path d="M15 44 L50 38 L85 44" fill="none" stroke="currentColor" strokeWidth="2"/>
          <line x1="15" y1="50" x2="85" y2="50" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      "conical-15": (
        <svg viewBox="0 0 100 60" className="w-full h-12">
          {/* 15° conical head - shallow triangle (h ≈ 9 at width 70) */}
          <line x1="15" y1="50" x2="15" y2="42" stroke="currentColor" strokeWidth="2"/>
          <line x1="85" y1="50" x2="85" y2="42" stroke="currentColor" strokeWidth="2"/>
          <path d="M15 42 L50 33 L85 42" fill="none" stroke="currentColor" strokeWidth="2"/>
          <line x1="15" y1="50" x2="85" y2="50" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      "flat": (
        <svg viewBox="0 0 100 60" className="w-full h-12">
          {/* Flat head */}
          <line x1="15" y1="50" x2="85" y2="50" stroke="currentColor" strokeWidth="2"/>
          {/* Vertical edges */}
          <line x1="15" y1="50" x2="15" y2="45" stroke="currentColor" strokeWidth="2"/>
          <line x1="85" y1="50" x2="85" y2="45" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    };
    return diagrams[type as keyof typeof diagrams] || null;
  };

  return (
    <div className="min-h-screen bg-page-modern">
      <Helmet>
        <title>Tank Volume Calculator - WLE Group</title>
        <meta name="description" content="Calculate tank volumes for cylindrical and rectangular tanks with different head types. Professional-grade calculations for ASME, Standard, 80:10 Flanged & Dished, Hemispherical, and Elliptical heads." />
      </Helmet>
      
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-24">
        <div className="container">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Calculator className="h-12 w-12 text-wle-blue" />
              <h1 className="text-5xl font-bold text-foreground">
                Tank Volume <span className="text-wle-blue">Calculator</span>
              </h1>
            </div>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Calculate precise volumes for cylindrical and rectangular tanks with different head configurations. 
              Professional-grade calculations for pressure vessel heads and tank fabrication.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16 bg-subtle-texture">
        <div className="container max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Card */}
            <Card className="h-fit">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Tank Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Tank Type Selection */}
                <div className="space-y-2">
                  <Label>Tank Type</Label>
                  <Select value={tankType} onValueChange={setTankType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select tank type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cylindrical">Cylindrical Tank</SelectItem>
                      <SelectItem value="rectangular">Rectangular Tank</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Tank Orientation */}
                <div className="space-y-2">
                  <Label>Tank Orientation</Label>
                  <Select value={tankOrientation} onValueChange={setTankOrientation}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select orientation" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vertical">Vertical</SelectItem>
                      <SelectItem value="horizontal">Horizontal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Tabs value={tankType} className="w-full">
                  <TabsContent value="cylindrical" className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="diameter">Inside Diameter (mm)</Label>
                      <Input
                        id="diameter"
                        type="number"
                        placeholder="e.g. 1000"
                        value={diameter}
                        onChange={(e) => setDiameter(e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="shell-height">Straight Shell Height (mm)</Label>
                      <Input
                        id="shell-height"
                        type="number"
                        placeholder="e.g. 2000"
                        value={shellHeight}
                        onChange={(e) => setShellHeight(e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="head-type">Head Type (Optional)</Label>
                      <Select value={headType} onValueChange={setHeadType}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select head type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Head</SelectItem>
                          {headTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              <div className="flex items-center gap-3">
                                <div className="w-16 h-8">
                                  {getHeadTypeDiagram(type.value)}
                                </div>
                                <span>{type.label}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {headType && headType !== "none" && (
                      <div className="p-4 bg-accent rounded-lg">
                        <h4 className="font-medium mb-2">Selected Head Type:</h4>
                        <div className="flex items-center gap-4 mb-2">
                          <div className="w-24 h-12">
                            {getHeadTypeDiagram(headType)}
                          </div>
                          <span className="text-sm font-medium">
                            {headTypes.find(t => t.value === headType)?.label}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {headTypes.find(t => t.value === headType)?.description}
                        </p>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="rectangular" className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="length">Length (mm)</Label>
                      <Input
                        id="length"
                        type="number"
                        placeholder="e.g. 2000"
                        value={length}
                        onChange={(e) => setLength(e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="width">Width (mm)</Label>
                      <Input
                        id="width"
                        type="number"
                        placeholder="e.g. 1000"
                        value={width}
                        onChange={(e) => setWidth(e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="height">Height (mm)</Label>
                      <Input
                        id="height"
                        type="number"
                        placeholder="e.g. 1500"
                        value={height}
                        onChange={(e) => setHeight(e.target.value)}
                      />
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            {/* Results Card */}
            <Card className="h-fit">
              <CardHeader>
                <CardTitle>Calculation Results</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4">
                  <div className="flex justify-between items-center p-4 bg-accent rounded-lg">
                    <span className="font-medium">Shell Volume:</span>
                    <span className="text-lg font-bold text-wle-blue">{results.shellVolume} L</span>
                  </div>
                  
                  {tankType === "cylindrical" && (
                    <div className="flex justify-between items-center p-4 bg-accent rounded-lg">
                      <span className="font-medium">Head Volume:</span>
                      <span className="text-lg font-bold text-wle-blue">{results.headVolume} L</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center p-4 bg-gradient-primary text-white rounded-lg">
                    <span className="font-medium">Total Volume:</span>
                    <div className="text-right">
                      <div className="text-xl font-bold">{results.totalVolume} L</div>
                      <div className="text-sm opacity-90">{results.totalVolumeCubicMeters} m³</div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button onClick={copyResults} variant="outline" className="flex-1">
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Results
                  </Button>
                  <Button onClick={printResults} variant="outline" className="flex-1">
                    <Printer className="h-4 w-4 mr-2" />
                    Print
                  </Button>
                </div>

                {results.totalVolume > 0 && (
                  <div className="mt-6 p-4 bg-blue-50 border-l-4 border-wle-blue rounded">
                    <h4 className="font-medium text-wle-blue mb-2">Calculation Summary</h4>
                    <div className="text-sm space-y-1">
                      <p><strong>Tank Type:</strong> {tankType === "cylindrical" ? "Cylindrical" : "Rectangular"}</p>
                      <p><strong>Orientation:</strong> {tankOrientation === "vertical" ? "Vertical" : "Horizontal"}</p>
                      {tankType === "cylindrical" && (
                        <>
                          <p><strong>Diameter:</strong> {diameter} mm</p>
                          <p><strong>Shell Height:</strong> {shellHeight} mm</p>
                          {headType && headType !== "none" && <p><strong>Head Type:</strong> {headTypes.find(t => t.value === headType)?.label}</p>}
                        </>
                      )}
                      {tankType === "rectangular" && (
                        <>
                          <p><strong>Dimensions:</strong> {length} × {width} × {height} mm</p>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-grid-pattern">
        <div className="container max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">Head Type Information</h2>
            <p className="text-muted-foreground">Understanding different tank head configurations and their applications</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {headTypes.map((type) => (
              <Card key={type.value} className="text-center">
                <CardHeader>
                  <div className="w-20 h-16 mx-auto mb-3">
                    {getHeadTypeDiagram(type.value)}
                  </div>
                  <CardTitle className="text-lg">{type.label}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    {type.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Engineering Disclaimer */}
      <section className="py-12 bg-muted/50">
        <div className="container max-w-4xl">
          <Card className="border-wle-blue/20">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <Info className="h-6 w-6 text-wle-blue flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg mb-2">🔍 Engineering Disclaimer</h3>
                  <p className="text-muted-foreground mb-3">
                    These tools provide <strong>accurate preliminary calculations</strong> for design and planning purposes. 
                    The formulas used are based on industry-standard engineering practices and ASME standards.
                  </p>
                  <p className="text-muted-foreground mb-3">
                    For <strong>final specifications, detailed engineering analysis, and professional certifications</strong>, 
                    we strongly recommend consulting with our experienced engineering team.
                  </p>
                  <div className="p-3 bg-wle-red/10 border border-wle-red/20 rounded-lg">
                    <p className="text-sm font-medium text-wle-red">
                      ⚠️ WLE is not responsible for any results used directly for manufacturing or installation 
                      without proper engineering review and approval.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TankVolumeCalculator;